import { useIdsSelect } from '@entities/common/IdsSelector/index.ts';
import { useIdsSelecting } from '@entities/common/IdsSelector/models.ts';
import { createEntityAdapter, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@shared/hooks';
import type { Group } from '@shared/types/common';

const groupAdapter = createEntityAdapter({
  selectId: (group: Group) => group.id,
});

const groupSlice = createSlice({
  name: 'common/groups',
  initialState: groupAdapter.getInitialState({
    selectedGroups: [] as Group[],
  }),
  reducers: {
    groupAdd: groupAdapter.addOne,
    groupsAdd: groupAdapter.addMany,
    groupsRemove: groupAdapter.removeAll,
    groupRemove: groupAdapter.removeOne,
    groupsSet: groupAdapter.setAll,
    groupSet: groupAdapter.setOne,
    groupUpdate: groupAdapter.updateOne,
    groupsReceived(state, action) {
      groupAdapter.setAll(state, action.payload.common.groups);
    },
    selectedGroupsSet: (state, action: PayloadAction<Group[]>) => {
      state.selectedGroups = [...action.payload];
    },
    resetSelectedGroups: (state) => {
      state.selectedGroups = [];
    },
  },
});

export const groupReducer = groupSlice.reducer;
export const groupActions = groupSlice.actions;

const groupSelectors = groupAdapter.getSelectors();

export const useGroup = (id: number) => {
  const dispatch = useAppDispatch();

  const group = useAppSelector((state) => groupSelectors.selectById(state.common.groups, id));

  const addGroup = (group: Group) => dispatch(groupActions.groupAdd(group));

  const ids = useAppSelector((state) => groupSelectors.selectIds(state.common.groups));

  const updateGroup = (id: number, changes: Partial<Group>) =>
    dispatch(groupActions.groupUpdate({ id: id, changes: changes }));

  const setGroup = (group: Group) => dispatch(groupActions.groupSet(group));

  const removeGroup = (id: number) => dispatch(groupActions.groupRemove(id));

  return {
    group,
    addGroup,
    setGroup,
    removeGroup,
    updateGroup,
    ids,
  };
};

export const useGroups = () => {
  const dispatch = useAppDispatch();

  const [submitState, setSubmitState] = useState<boolean | undefined>(undefined);

  const addGroups = (groups: Group[]) => dispatch(groupActions.groupsAdd(groups));

  const groups = useAppSelector((state) => groupSelectors.selectAll(state.common.groups));
  const selectedGroups = useAppSelector((state) => state.common.groups.selectedGroups);

  const setSelectedGroups = useCallback(
    (groups: Group[]) => {
      dispatch(groupActions.selectedGroupsSet(groups));
    },
    [dispatch]
  );

  const setGroups = (groups: Group[]) => dispatch(groupActions.groupsSet(groups));
  const setSelectedGroup = (index: number, group: Group) => {
    const newSelectedGroup = [...selectedGroups];
    newSelectedGroup[index] = group;
    setSelectedGroups(newSelectedGroup);
  };

  const initialGroups = (groups: Group[]) => {
    dispatch(groupActions.selectedGroupsSet(groups));
  };

  const resetSelectedGroups = () => dispatch(groupActions.resetSelectedGroups());

  const onSelect = (index: number, group: Group) => {
    setSelectedGroup(index, group);
  };

  const isSelected = (index: number, group: Group) => {
    return selectedGroups[index]?.id === group.id;
  };

  ///
  /// для работы с выбором нескольких групп
  ///

  const setSelectedGroupsFunc = (groups: Group[]) => {
    setSelectedGroups(groups);
  };

  // для понимания нужно ли показывать кнопку Готово
  const [prevSelectedGroups, setPrevSelectedGroups] = useState<Group[]>([]);

  const setPrevSelectedGroupsFunc = (groups: Group[]) => {
    setPrevSelectedGroups(groups);
  };

  const resetPrevSelectedGroupsFunc = () => {
    setPrevSelectedGroups([]);
  };

  // переменная для галки Для всех
  const groupAll: Group | undefined =
    groups && groups.find((group) => group.special_type === 'all');

  // выбрана ли галка Для всех
  const isSelectedGroupAll = selectedGroups.includes(groupAll!);

  // инициализация начального состояния при формировании страницы
  const initialGroupsForPublication = useCallback(
    (groups: Group[], customsGroups: Group[], excludedGroups: Group[]) => {
      let result: Group[] = [];

      // resetSelectedGroups();

      // если выбрана галка Для всех и есть группы исключения
      // customsGroups.length === 0 && excludedGroups.length === 0 &&
      // result = groups.
      // console.log('sortedGroups:', groups);
      if (excludedGroups.length) {
        const idsExcludedGroups = excludedGroups.map((g) => g.id);
        result = groups.filter((g) => !idsExcludedGroups.includes(g.id));
      }

      // если выбраны конкретные группы

      if (customsGroups.length) {
        const idsCustomGroups = customsGroups.map((g) => g.id);
        result = groups.filter((g) => idsCustomGroups.includes(g.id));
      }

      // если нет исключений, и кастомные тоже пусты => выбраны все группы

      if (excludedGroups.length === 0 && customsGroups.length === 0) {
        result = groups;
      }

      setSelectedGroups(result);
      setPrevSelectedGroups(result);
    },
    [setSelectedGroups]
  );

  // проверка выбрана та или иная группа
  const isSelectedAtAnyGroups = (group: Group) => {
    return (
      selectedGroups.find((selectedGroup: Group) => selectedGroup.id === group.id) !== undefined
    );
  };

  // удалние группы из выбранных
  const removeFromSelectedGroups = (group: Group) => {
    const newSelectedGroups = selectedGroups.filter((selectedGroup: Group) => {
      if (selectedGroup?.id !== group?.id) return selectedGroup;
    });
    setSelectedGroups(newSelectedGroups);
  };

  // добавление группы в выбранные
  const addSelectedToGroups = (group: Group) => {
    if (!isSelectedAtAnyGroups(group)) setSelectedGroups([...selectedGroups, group]);
  };

  // список исключенных групп, когда галка Для всех выбрана
  const excludedGroups: Group[] = isSelectedGroupAll
    ? groups.filter((group) => {
        if (!selectedGroups.includes(group) && group.special_type !== 'all') return group;
      })
    : [];

  // список названий групп для выпадающего списка при поиске
  const groupTitles = groups.map((group) => group.name);

  // сравнение двух списков групп
  const areArraysEqual = (groups1: Group[], groups2: Group[]): boolean => {
    if (groups1.length === groups2.length) {
      const arrIds1 = groups1.map((group1) => {
        return group1.id;
      });
      const arrIds2 = groups2.map((group2) => {
        return group2.id;
      });
      return arrIds1.every((arrId1) => arrIds2.includes(arrId1));
    }
    return false;
  };

  return {
    groups,
    groupTitles,
    selectedGroups,
    setGroups,
    addGroups,
    setSelectedGroup,
    setSelectedGroups,
    onSelect,
    isSelected,
    resetSelectedGroups,
    removeFromSelectedGroups,
    addSelectedToGroups,
    isSelectedAtAnyGroups,
    initialGroups,
    isSelectedGroupAll,
    groupAll,
    excludedGroups,
    prevSelectedGroups,
    resetPrevSelectedGroupsFunc,
    setPrevSelectedGroups,
    setPrevSelectedGroupsFunc,
    initialGroupsForPublication,
    areArraysEqual,
    setSelectedGroupsFunc,
    submitState,
    setSubmitState,
  };
};
export const useGroupsSelecting = () => {
  const { selectedIds: selectedGroupsIds, doResetSelectedIds: resetSelectedGroupsIds } =
    useIdsSelect('groups');
  const { isSelecting, setIsSelectingTrue, setIsSelectingFalse } = useIdsSelecting('groups');
  return {
    selectedGroupsIds: selectedGroupsIds || [],
    resetSelectedGroupsIds,
    isSelecting,
    setIsSelectingTrue,
    setIsSelectingFalse,
  };
};
